import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useQuery } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  ItemAction,
  Pill,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import {
  SortDirection,
  UserGroupMembershipType,
  UserWithGroupsType,
  UsersAdapter,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useMemo } from "react";
import styles from "./UserGroupFormPanel.module.scss";

const cx = classNames.bind(styles);

export const UserSelectionTablePanel = ({
  selectedUsers,
  addUser,
  filterConfigStorageKey,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  selectedUsers: Array<UserGroupMembershipType["user"]>;
  addUser: (newUser: UserWithGroupsType) => void;
}) => {
  const [tableConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["table-configuration", "table-state"],
    "user-selection-table"
  );
  const [filters] = useFilters<{ search: string }>(filterConfigStorageKey);
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const queryPayload = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      isAlreadyFormatted: true,
      ...(filters.search
        ? {
            $or: [
              { name: { $like: filters.search } as any },
              { email: { $like: filters.search } as any },
            ],
          }
        : {}),
    },
  };

  const { data, isLoading } = useQuery(
    ["users", "v2", queryPayload],
    () => UsersAdapter.getList(queryPayload),
    {
      staleTime: 3 * 60 * 1000,
    }
  );

  const items = data?.data ?? [];

  const selectedUserIds = useMemo(
    () => selectedUsers.map(({ id }) => id),
    [selectedUsers]
  );

  const getItemActions = ({
    item,
  }: {
    item: UserWithGroupsType;
  }): ItemAction[] =>
    selectedUserIds.includes(item.id)
      ? []
      : [
          {
            label: "Add User",
            buttonProps: {
              onClick: () => addUser(item),
              icon: "plus-circle",
            },
          },
        ];

  return (
    <div className={cx("userSelectionContainer")}>
      <div className={cx("userTableContainer")}>
        <DataTablePanel
          storageKey={tableConfigStorageKey}
          panelProps={{
            title: "All Users",
          }}
          dataTableProps={{
            headers: [
              { key: "name", label: "Name" },
              {
                key: "email",
                label: "Email",
                renderComponent: ({ item }: { item: UserWithGroupsType }) =>
                  item.email ?? "—",
              },
              {
                label: "Status",
                key: "status",
                isSortable: true,
                renderComponent: ({ item }: { item: UserWithGroupsType }) => (
                  <Pill
                    variant={item.status === "active" ? "success" : "default"}
                  >
                    {toTitleCaseString(item.status, {
                      intent: "ui",
                      exceptions: ["oil-and-gas"],
                    })}
                  </Pill>
                ),
              },
              {
                label: "Effective Date",
                key: "created_at",
                isSortable: true,
                renderComponent: ({ item }: { item: UserWithGroupsType }) => (
                  <DataTable.DataRow.DateCell value={item.created_at} />
                ),
              },
            ],
            sorting: {
              sortBy: tableState.sortBy,
              sortDirection: tableState.sortDirection,
            },
            isLoading,
            items,
            pagination: {
              page: tableState.page,
              pageSize: tableState.pageSize,
              total: data?.total_entries,
            },
            getItemId: (item) => item.id,
            onSortChange: updateTableState,
            onPaginationChange: updateTableState,
            isFluid: false,
            getItemActions,
          }}
        />
      </div>
    </div>
  );
};
